import MobileDetect from 'mobile-detect';
import { DeviceTypes } from '@/typings/enums/enums';
import store from '@/store';
import { Cache } from '@/services/cache';
import { isSsr } from '@/utilities/Utilities';

export enum Device {
  DESKTOP = 'desktop',
  TABLET = 'tablet',
  MOBILE = 'mobile'
}

export interface DeviceData<Data> {
  desktop?: Data;
  tablet?: Data;
  mobile?: Data;
}

export interface DeviceDataDesktop {
  overwrite_tablet?: string | boolean;
  overwrite_mobile?: string | boolean;
}

interface LookupData<Data> {
  desktop?: DeviceDataDesktop & Data;
  tablet?: Data;
  mobile?: Data;
}

function isTouchDevice() {
  if (!isSsr()) {
    return 'ontouchstart' in window;
  }
  return false;
}

function isIOSTablet() {
  if (!isSsr()) {
    return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1 && !window.MSStream;
  } else {
    return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  }
}

function useDevice() {
  // Get user agent by prioritizing the one from the navigator. If not available we get it from utility
  // state. This allows e.g. SSR to set the userAgent to render the page.
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : store.getters['utility/userAgent'] ?? '';

  // Cache the mobile-detect object in our cache storage so we don't continue
  // to re-initialize new objects.
  const md = Cache.get<MobileDetect>('mobile-detect') ?? new MobileDetect(userAgent);
  Cache.set('mobile-detect', md);

  // Get current device from the store. This allows for mobile emulation.
  const currentDevice = store.getters['global/currentDevice'];

  let isMobile = !!md.mobile() || currentDevice === DeviceTypes.MOBILE;
  let isTablet = !!md.tablet() || currentDevice === DeviceTypes.TABLET;

  /**
   * Our package mobile-detect cant detect iPad version from 13+, which causes us to assume it is an iPad the user in coming from
   * in some other way - It seems from what I can find online, this is one of the most feasible solutions
   * Note: This will only apply if you are testing it from a real device or chrome emulator - If will not work
   * if you test it in a Desktop Safari browser with an iPad user agent, due to not being a touch device.
   */
  if (!isTablet && !isMobile && isTouchDevice() && isIOSTablet()) {
    isTablet = true;
  }

  const isDesktop = !isMobile && !isTablet;

  isMobile = isMobile && !isTablet;
  isTablet = isTablet && !isMobile;

  return {
    isMobile,
    isTablet,
    isDesktop
  };
}

/**
 * Locate device specific data in a known data format. This will when you have desktop, tablet & mobile
 * specific settings automatically fetch the correct one depending on the settings & current device.
 */
export function getDeviceData<Data>(
  data: LookupData<Data>,
  overwriteMobileName: keyof (DeviceDataDesktop & Data) = 'overwrite_mobile',
  overwriteTabletName: keyof (DeviceDataDesktop & Data) = 'overwrite_tablet'
): Data | undefined {
  const desktopData = data.desktop;
  const { isMobile, isTablet } = useDevice();

  const overwriteMobile =
    desktopData === undefined ||
    // eslint-disable-next-line security/detect-object-injection
    typeof desktopData[overwriteMobileName] === 'undefined' ||
    // eslint-disable-next-line security/detect-object-injection
    desktopData[overwriteMobileName] + '' === '1';

  const overwriteTablet =
    desktopData === undefined ||
    // eslint-disable-next-line security/detect-object-injection
    typeof desktopData[overwriteTabletName] === 'undefined' ||
    // eslint-disable-next-line security/detect-object-injection
    desktopData[overwriteTabletName] + '' === '1';

  if (isTablet && !overwriteTablet) {
    return data.tablet;
  }

  if (isMobile && !overwriteMobile) {
    return data.mobile;
  }

  return desktopData;
}

export function getDevice(): Device {
  const { isTablet, isMobile } = useDevice();

  if (isMobile) {
    return Device.MOBILE;
  }

  if (isTablet) {
    return Device.TABLET;
  }

  return Device.DESKTOP;
}

/**
 * return container height based on current device (simulating or actual device)
 */
export const getDeviceHeight = () => {
  if (isSsr()) {
    return 0;
  }

  let height = store.getters['utility/windowHeight'] ?? window.innerHeight;

  const currentDevice = store.getters['global/currentDevice'];
  const isSimulating = store.getters['utility/bodyClassList'].some((className) => className.includes('site--simulate'));

  if (isSimulating && currentDevice === DeviceTypes.MOBILE) {
    height = 667;
  } else if (isSimulating && currentDevice === DeviceTypes.TABLET) {
    height = 1024;
  }

  return height;
};

export default useDevice;
